import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowRight, Loader2, FileText, BookOpen, Receipt, Users,
  Download, Sparkles, Bookmark, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

function money(n, currency = "USD") {
  const num = Number(n || 0);
  const abs = Math.abs(num);
  const sign = num < 0 ? "-" : "";
  try {
    return `${sign}${abs.toLocaleString(undefined, { style: "currency", currency })}`;
  } catch {
    return `${sign}$${abs.toFixed(2)}`;
  }
}

/**
 * Reusable drill-down modal. Fetches /accounting/drill with the given params
 * and renders a scrollable table of source records. Each row is clickable and
 * routes to the underlying Transaction / Journal Entry / Invoice / Bill.
 *
 * Props:
 *   open, onClose
 *   title           — display title in modal header (e.g. "Food · $245.67")
 *   params          — {kind, category?, account_id?, customer_id?, vendor_id?, bucket?, start?, end?, as_of?}
 *   currency        — currency code for money formatting
 */
export default function DrillDownModal({ open, onClose, title, params, currency = "USD" }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  // iter 72p: three actions in the modal footer.
  const [aiSummary, setAiSummary] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiModel, setAiModel] = useState("claude");
  const [saveName, setSaveName] = useState("");
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    if (!params?.kind) return;
    setLoading(true);
    setError(null);
    setAiSummary(null);
    setSaveName("");
    try {
      const { data } = await api.get("/accounting/drill", { params });
      setData(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't load drill-down");
      toast.error("Drill-down failed");
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const exportCsv = async () => {
    if (!params?.kind) return;
    setExporting(true);
    try {
      const resp = await api.get("/accounting/drill.csv", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `drill-${params.kind}-${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "CSV export failed");
    } finally { setExporting(false); }
  };

  const explain = async () => {
    if (!data) return;
    setAiLoading(true);
    try {
      // Send a compact snapshot to the LLM — only top 20 rows.
      const rows = (data.journal_lines?.length ? data.journal_lines : data.transactions || []).slice(0, 20);
      const { data: r } = await api.post("/accounting/drill/explain", {
        kind: data.kind,
        label: data.label || title,
        period: data.period,
        count: data.count,
        total: data.total ?? (data.total_debit - data.total_credit),
        currency,
        rows,
        model: aiModel,
      });
      setAiSummary(r);
    } catch (err) {
      const detail = err?.response?.data?.detail || "AI explain failed";
      setAiSummary({ summary: detail, model: "error" });
      toast.error(detail);
    } finally { setAiLoading(false); }
  };

  const saveShortcut = async () => {
    const name = saveName.trim();
    if (!name) return toast.error("Give this drill a short name");
    setSaving(true);
    try {
      await api.post("/saved-drills", { name, params, currency });
      toast.success(`Saved "${name}" to your dashboard`);
      setSaveName("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save drill");
    } finally { setSaving(false); }
  };

  const goToSource = (link) => {
    if (!link) return;
    onClose?.();
    setTimeout(() => {
      switch (link.kind) {
        case "transaction":
          navigate(`/transactions?highlight=${link.id}`);
          break;
        case "journal_entry":
          navigate(`/chart-of-accounts?tab=journal&highlight=${link.id}`);
          break;
        case "invoice":
          navigate(`/receivables?tab=invoices&highlight=${link.id}`);
          break;
        case "bill":
          navigate(`/payables?tab=bills&highlight=${link.id}`);
          break;
        default:
          break;
      }
    }, 100);
  };

  const isJE = data?.journal_lines?.length > 0;
  const isAging = data?.kind === "receivable" || data?.kind === "payable";
  const rows = isJE ? data.journal_lines : data?.transactions || [];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="drill-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isJE ? <BookOpen className="w-4 h-4 text-[var(--brand)]" />
              : data?.kind === "receivable" ? <Users className="w-4 h-4 text-[var(--brand)]" />
              : data?.kind === "payable" ? <Receipt className="w-4 h-4 text-[var(--brand)]" />
              : <FileText className="w-4 h-4 text-[var(--brand)]" />}
            <span className="truncate">{title || "Details"}</span>
          </DialogTitle>
          <DialogDescription className="flex items-center gap-4 text-xs">
            {loading ? (
              <span className="flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</span>
            ) : data ? (
              <>
                <span data-testid="drill-count">{data.count} record{data.count === 1 ? "" : "s"}</span>
                {isJE ? (
                  <>
                    <span className="text-[var(--muted-text)]">Dr {money(data.total_debit, currency)}</span>
                    <span className="text-[var(--muted-text)]">Cr {money(data.total_credit, currency)}</span>
                  </>
                ) : (
                  <span data-testid="drill-total" className="font-medium">Total {money(data.total, currency)}</span>
                )}
              </>
            ) : null}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-auto -mx-6 px-6">
          {error && (
            <div className="text-sm text-[var(--expense)] p-4 border border-[var(--expense)]/30 rounded-md">
              {error}
            </div>
          )}
          {!loading && !error && rows.length === 0 && (
            <div className="text-center py-10 text-sm text-[var(--muted-text)]">
              No records for this line.
            </div>
          )}
          {rows.length > 0 && (
            <table className="qb-table w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-[var(--muted-text)] border-b border-[var(--border)]">
                  <th className="px-2 py-2 font-medium">Date</th>
                  {isJE ? (
                    <>
                      <th className="px-2 py-2 font-medium">Memo</th>
                      <th className="px-2 py-2 font-medium text-right">Debit</th>
                      <th className="px-2 py-2 font-medium text-right">Credit</th>
                    </>
                  ) : isAging ? (
                    <>
                      <th className="px-2 py-2 font-medium">Number</th>
                      <th className="px-2 py-2 font-medium">Counterparty</th>
                      <th className="px-2 py-2 font-medium">Due</th>
                      <th className="px-2 py-2 font-medium">Bucket</th>
                      <th className="px-2 py-2 font-medium text-right">Balance</th>
                    </>
                  ) : (
                    <>
                      <th className="px-2 py-2 font-medium">Description</th>
                      <th className="px-2 py-2 font-medium">Type</th>
                      <th className="px-2 py-2 font-medium">Source</th>
                      <th className="px-2 py-2 font-medium text-right">Amount</th>
                    </>
                  )}
                  <th className="px-2 py-2 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr
                    key={`${r.id}-${i}`}
                    data-testid={`drill-row-${r.id}`}
                    onClick={() => goToSource(r.link)}
                    className="cursor-pointer"
                    title="Click to open source"
                  >
                    <td className="px-2 py-2 font-mono-data text-xs whitespace-nowrap">
                      {(() => {
                        try { return format(new Date(r.date), "MMM d, yyyy"); }
                        catch { return r.date; }
                      })()}
                    </td>
                    {isJE ? (
                      <>
                        <td className="px-2 py-2">
                          <div className="font-medium truncate max-w-[280px]">{r.memo || "—"}</div>
                          {r.line_memo && <div className="text-xs text-[var(--muted-text)] truncate max-w-[280px]">{r.line_memo}</div>}
                        </td>
                        <td className="px-2 py-2 text-right font-mono-data">{r.debit ? money(r.debit, currency) : ""}</td>
                        <td className="px-2 py-2 text-right font-mono-data">{r.credit ? money(r.credit, currency) : ""}</td>
                      </>
                    ) : isAging ? (
                      <>
                        <td className="px-2 py-2 font-mono-data text-xs">{r.number}</td>
                        <td className="px-2 py-2 truncate max-w-[180px]">{r.counterparty || "—"}</td>
                        <td className="px-2 py-2 font-mono-data text-xs">{r.due_date}</td>
                        <td className="px-2 py-2">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                            r.bucket === "current" ? "bg-[var(--brand-light)] text-[var(--brand-text)]" :
                            r.bucket === "90+" ? "bg-red-100 text-red-800" :
                            "bg-amber-100 text-amber-800"
                          }`}>{r.bucket}</span>
                        </td>
                        <td className="px-2 py-2 text-right font-mono-data font-medium">{money(r.balance, currency)}</td>
                      </>
                    ) : (
                      <>
                        <td className="px-2 py-2 truncate max-w-[260px]">
                          {r.is_transfer && (
                            <span data-testid="drill-transfer-badge"
                                  className="text-[9px] mr-1.5 px-1.5 py-0.5 rounded-full uppercase tracking-widest bg-[#EEF2FA] text-[#1F3A6B] font-semibold">
                              Transfer
                            </span>
                          )}
                          {r.note || <span className="text-[var(--muted-text)]">—</span>}
                        </td>
                        <td className="px-2 py-2">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                            r.type === "income" ? "bg-[var(--brand-light)] text-[var(--brand-text)]" :
                            r.type === "saving" ? "bg-amber-100 text-amber-800" :
                            "bg-red-100 text-red-800"
                          }`}>{r.type}</span>
                        </td>
                        <td className="px-2 py-2 text-xs text-[var(--muted-text)]">{r.source_provider || "manual"}</td>
                        <td className="px-2 py-2 text-right font-mono-data font-medium">{money(r.amount, currency)}</td>
                      </>
                    )}
                    <td className="px-2 py-2 text-[var(--muted-text)]">
                      <ArrowRight className="w-3.5 h-3.5" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* iter 72p: AI Explain result banner (visible only after Explain runs) */}
        {aiSummary && (
          <div data-testid="drill-ai-summary"
               className={`mx-1 mt-3 px-3 py-2 rounded-md border text-sm ${
                 aiSummary.model === "error"
                   ? "bg-[#FDECEC] border-[#F5C6CB] text-[#B4283A]"
                   : "bg-[#EEF7F2] border-[#CFE5D8] text-[#2F4F4F]"
               }`}>
            <div className="flex items-start gap-2">
              <Sparkles className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <div className="flex-1">
                <div>{aiSummary.summary}</div>
                {aiSummary.model && aiSummary.model !== "error" && (
                  <div className="text-[10px] text-[var(--muted-text)] mt-0.5 tracking-widest uppercase">
                    {aiSummary.model}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="pt-4 flex justify-between items-center border-t border-[var(--border)] flex-wrap gap-2">
          <div className="flex items-center gap-1.5 flex-wrap">
            {/* Explain (AI) with model picker */}
            <div className="inline-flex rounded-md border border-[var(--border)] overflow-hidden">
              <Button variant="ghost" size="sm" onClick={explain}
                      disabled={aiLoading || !data || (data?.count === 0)}
                      data-testid="drill-explain-btn"
                      className="h-8 rounded-none border-r border-[var(--border)] gap-1.5">
                {aiLoading
                  ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Thinking…</>
                  : <><Sparkles className="w-3.5 h-3.5" /> Explain</>}
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" data-testid="drill-explain-model"
                          className="h-8 rounded-none px-2 text-[10px] uppercase tracking-widest">
                    {aiModel} <ChevronDown className="w-3 h-3 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem data-testid="drill-model-claude"
                                    onClick={() => setAiModel("claude")}>Claude Sonnet 5</DropdownMenuItem>
                  <DropdownMenuItem data-testid="drill-model-gpt"
                                    onClick={() => setAiModel("gpt")}>GPT 5.6 Terra</DropdownMenuItem>
                  <DropdownMenuItem data-testid="drill-model-gemini"
                                    onClick={() => setAiModel("gemini")}>Gemini 3 Flash</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            {/* CSV Export */}
            <Button variant="ghost" size="sm" onClick={exportCsv}
                    disabled={exporting || !data || (data?.count === 0)}
                    data-testid="drill-export-csv"
                    className="h-8 gap-1.5">
              <Download className="w-3.5 h-3.5" /> {exporting ? "Building…" : "CSV"}
            </Button>
            {/* Save as shortcut */}
            <div className="inline-flex items-center gap-1">
              <Input value={saveName} onChange={(e) => setSaveName(e.target.value)}
                     placeholder="Name for dashboard shortcut"
                     data-testid="drill-save-name"
                     className="h-8 text-xs w-44" />
              <Button variant="ghost" size="sm" onClick={saveShortcut}
                      disabled={saving || !saveName.trim()}
                      data-testid="drill-save-btn"
                      className="h-8 gap-1.5">
                <Bookmark className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
          <Button variant="outline" onClick={onClose} data-testid="drill-close" className="rounded-md">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
